import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const timerKey = 'alteire-cart-timer-v1';
let timerDeadline = 0;
let upsellPending = false;

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise?.then(({ cart, detail }) => {
    if ((cart?.totalQuantity ?? detail?.itemCount) === 0 && !detail?.didError) {
      timerDeadline = 0;
      try { sessionStorage.removeItem(timerKey); } catch {}
    }
  }).catch(() => {});
});

class AlteireCartUpsell extends HTMLElement {
  connectedCallback() { this.addEventListener('click', this.addBracelet); }
  disconnectedCallback() { this.removeEventListener('click', this.addBracelet); }

  addBracelet = async (event) => {
    const button = event.target.closest('button[data-variant-id]');
    if (!button || !this.contains(button) || upsellPending || button.disabled) return;
    const errorElement = this.querySelector('[role="alert"]');
    const oldLabel = button.innerHTML;
    const quantity = Number(button.dataset.quantity) || 1;
    const drawer = this.closest('theme-drawer');
    const oldCount = Number(drawer?.querySelector('[ref="cartItemCount"]')?.textContent) || 0;
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    upsellPending = true;
    this.setAttribute('aria-busy', 'true');
    for (const control of this.querySelectorAll('button')) control.disabled = true;
    button.textContent = this.dataset.adding;
    if (errorElement) errorElement.hidden = true;

    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'cart',
      lines: [{ merchandiseId: button.dataset.variantId, quantity }],
      promise: deferred.promise,
    }));

    let added = false;
    try {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(element => element.dataset.sectionId))].slice(0, 5);
      const response = await fetch(this.dataset.addUrl, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: button.dataset.variantId, quantity }], sections, sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : this.dataset.error);
      added = true;
      let ajaxCart;
      try {
        const cartResponse = await fetch(this.dataset.cartUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (cartResponse.ok) ajaxCart = await cartResponse.json();
      } catch {}
      const sectionDocument = new DOMParser().parseFromString(result.sections?.['cart-drawer-section'] || '', 'text/html');
      const renderedCount = sectionDocument.querySelector('[ref="cartItemCount"]')?.textContent;
      const itemCount = ajaxCart?.item_count ?? (renderedCount ? Number(renderedCount) : oldCount + quantity);
      deferred.resolve({
        ...(ajaxCart ? { cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart) } : {}),
        detail: { sections: result.sections, items: ajaxCart?.items, itemCount, didError: false, source: 'alteire-cart-upsell' },
      });
      button.textContent = this.dataset.added;
    } catch (error) {
      deferred.reject(error);
      if (errorElement) {
        errorElement.textContent = error.message || this.dataset.error;
        errorElement.hidden = false;
      }
      button.innerHTML = oldLabel;
    } finally {
      upsellPending = false;
      this.removeAttribute('aria-busy');
      for (const control of this.querySelectorAll('button')) control.disabled = added && control === button;
    }
  };
}

class AlteireCartTimer extends HTMLElement {
  connectedCallback() {
    this.drawer = this.closest('theme-drawer');
    this.drawer?.addEventListener('theme-drawer:open', this.start);
    if (this.drawer?.hasAttribute('open') || this.closest('dialog')?.open) this.start();
  }

  disconnectedCallback() {
    this.drawer?.removeEventListener('theme-drawer:open', this.start);
    clearInterval(this.interval);
  }

  start = () => {
    clearInterval(this.interval);
    try { timerDeadline = Number(sessionStorage.getItem(timerKey)) || timerDeadline; } catch {}
    if (!timerDeadline) {
      timerDeadline = Date.now() + Number(this.dataset.minutes) * 60000;
      try { sessionStorage.setItem(timerKey, String(timerDeadline)); } catch {}
    }
    this.tick();
    if (timerDeadline > Date.now()) this.interval = setInterval(this.tick, 1000);
  };

  tick = () => {
    if (timerDeadline <= Date.now()) {
      timerDeadline = Date.now() + Number(this.dataset.minutes) * 60000;
      try { sessionStorage.setItem(timerKey, String(timerDeadline)); } catch {}
    }
    const remaining = Math.max(0, Math.ceil((timerDeadline - Date.now()) / 1000));
    const value = this.querySelector('[data-timer-value]');
    if (value) {
      value.hidden = false;
      value.textContent = `${String(Math.floor(remaining / 60)).padStart(2, '0')}:${String(remaining % 60).padStart(2, '0')}`;
    }
  };
}

if (!customElements.get('alteire-cart-upsell')) customElements.define('alteire-cart-upsell', AlteireCartUpsell);
if (!customElements.get('alteire-cart-timer')) customElements.define('alteire-cart-timer', AlteireCartTimer);
